import { objects } from "../utils/objects.js";
import { createSingletonLogger } from "./singleton-logger.js";
import { store } from "./store.js";

class Descriptors {
  static componentName = "descriptors";
  #descriptors = {};
  #methodDescriptors = {};
  #defaultConfigs = {};

  constructor() {
    createSingletonLogger(this);
    store.set("descriptors", this.#descriptors);
  }

  get(namespace) {
    return this.#descriptors[namespace];
  }

  getDefaultConfig(key) {
    return this.#defaultConfigs[key];
  }

  getMethodDescriptor(key) {
    return this.#methodDescriptors[key];
  }

  create(descriptorData) {
    if (!descriptorData.namespace) {
      this.error("Descriptor doesn't have a namespace, skipping");
      return;
    }

    const descriptorSuper = this.get(descriptorData.extends);

    if (!descriptorSuper && descriptorData.extends) {
      this.error(
        `Descriptor [${descriptorData.namespace}] tries to extend non-existing namespace [${descriptorData.extends}], skipping`,
      );
      return;
    }

    const descriptor = descriptorSuper
      ? objects.clone(descriptorSuper)
      : {
          properties: {},
          methods: {},
        };

    // Store extends for implementation fallback lookup
    if (descriptorData.extends) {
      descriptor.extends = descriptorData.extends;
    }

    for (const property of descriptorData.properties || []) {
      if (property.name.startsWith("_")) {
        continue;
      }

      if (property.type === "group") {
        descriptor.properties[property.name] = {
          name: property.name,
          type: "group",
          optional: property.optional || false,
          dynamic: property.dynamic || false,
          readOnly: property.readOnly || false,
        };

        for (const childProperty of property.properties || []) {
          if (childProperty.name.startsWith("_")) {
            continue;
          }

          const key = `${property.name}.${childProperty.name}`;
          descriptor.properties[key] = {
            ...childProperty,
            name: key,
            group: property.name,
            optional: childProperty.optional || false,
            readOnly: childProperty.readOnly || property.readOnly || false,
          };
        }
        continue;
      }

      descriptor.properties[property.name] = property;
    }

    for (const method of descriptorData.methods || []) {
      if (method.name.startsWith("_")) {
        continue;
      }

      descriptor.methods[method.name] = method;
    }

    this._addDefaultConfig(descriptor, descriptorData.namespace);
    this._addMethodConfigs(descriptor, descriptorData.namespace);

    this.#descriptors[descriptorData.namespace] = descriptor;
    return descriptor;
  }

  remove(namespace) {
    delete this.#descriptors[namespace];
  }

  // mostly exists for testing purposes. unlikely to be needed in development
  clear() {
    this.#defaultConfigs = {};
    this.#descriptors = {};
    this.#methodDescriptors = {};
  }

  _addDefaultConfig(descriptor, namespace) {
    const config = {};
    const properties = Object.values(descriptor.properties);

    for (let property of properties) {
      if (property.type === "group") {
        continue;
      }

      if (property.group && descriptor.properties[property.group].optional) {
        continue;
      }

      if (property.optional || property.readOnly) {
        continue;
      }

      config[property.name] = property.default;
    }

    this.#defaultConfigs[namespace] = config;
  }

  _addMethodConfigs(descriptor, namespace) {
    for (const method of Object.values(descriptor.methods)) {
      const methodName = `${namespace}:${method.name}`;

      const config = {
        default: {},
        descriptor: {
          properties: {},
        },
      };

      for (let property of method.properties) {
        const descriptor = {
          properties: {
            ...config.descriptor.properties,
            [property.name]: property,
          },
        };

        if (property.optional) {
          config.descriptor = descriptor;
          continue;
        }

        config.default[property.name] = property.default;
        config.descriptor = descriptor;
      }

      this.#defaultConfigs[methodName] = config.default;
      this.#methodDescriptors[methodName] = config.descriptor;
    }
  }
}
const descriptors = new Descriptors();
export { Descriptors, descriptors };